<template>
  <div>
    <!-- An Entry -->
    <div
      class="entry"
      v-if="entry.visible"
      :style="entryStyle"
      :class="entryCssClass"
    >
      <div
        v-if="entry.children"
        class="entry-element caret"
        @click.stop="toggleOpenClose"
      >
        <font-awesome-icon
          icon="caret-right"
          class="fa-fw caret-icon"
          :class="{ 'caret-open': entry.open }"
        />
      </div>
      <div v-if="!noCheckIcon" class="entry-element check" @click="select">
        <checkbox :state="entry.state"></checkbox>
      </div>
      <div
        v-if="!noIcon && entry.children"
        class="entry-element icon-folder"
        @click="select"
      >
        <font-awesome-icon v-if="!entry.open" icon="folder" class="fa-fw" />
        <font-awesome-icon v-if="entry.open" icon="folder-open" class="fa-fw" />
      </div>
      <div class="entry-element" @click="select">
        <slot :vm="this" :entry="entry">{{ entry.name }}</slot>
      </div>
    </div>

    <!-- Child Entries (List of this component recursively) -->
    <!--   This child container is always visible for height transition -->
    <div class="children-container" :style="childrenContainerStyle">
      <transition
        @before-enter="beforeChildrenEnter"
        @before-leave="beforeChildrenLeave"
      >
        <div v-if="entry.open">
          <tree-view-entry
            ref="children"
            v-for="child of entry.children"
            :key="child.code"
            :height="height"
            :entry="child"
            :level="level + 1"
            :no-icon="noIcon"
            :no-check-icon="noCheckIcon"
            :entry-state-change="entryStateChange"
            @open-change="handleChildOpenChange"
            @visible-change="handleChildVisibleChange"
            @entry-status-change="handleChildSelect"
          >
            <template slot-scope="_">
              <slot :vm="_.vm" :entry="_.entry">{{ _.entry.name }}</slot>
            </template>
          </tree-view-entry>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import Checkbox from '@/components/treeview/Checkbox.vue'

function switchState(fromState) {
  switch (fromState) {
    case 'none':
      return 'checked'
    case 'intermediate':
      return 'checked'
    case 'checked':
      return 'none'
  }
}

function walk(entry, f) {
  if (entry.children) {
    for (const child of entry.children) {
      walk(child, f)
    }
  }
  f(entry)
}

export default {
  name: 'tree-view-entry',
  components: {
    Checkbox,
  },
  props: {
    entry: Object,
    height: {
      type: Number,
      default: 30,
    },
    level: Number,
    noIcon: Boolean,
    noCheckIcon: Boolean,
    entryStateChange: Function,
  },
  watch: {
    'entry.state': {
      deep: false,
      handler() {
        this.$emit('entry-status-change', this.entry)
      },
    },
    'entry.visible': {
      deep: false,
      handler() {
        this.$emit('visible-change')
      },
    },
  },
  data() {
    return {
      childHeight: 0,
    }
  },
  computed: {
    entryStyle() {
      return {
        height: `${this.height}px`,
        paddingLeft: `${this.level * 20}px`,
      }
    },
    entryCssClass() {
      return {
        open: this.entry.open,
        'has-children': this.entry.children,
        selected: this.entry.state === 'checked',
      }
    },
    childrenContainerStyle() {
      return {
        height: `${this.childHeight}px`,
      }
    },
  },
  methods: {
    /**
     * Toggle open/close state of this entry
     */
    toggleOpenClose() {
      this.entry.open = !this.entry.open
    },
    /**
     * Select this entry
     */
    select() {
      const newState = switchState(this.entry.state)
      this.entry.state = newState
      walk(this.entry, entry => {
        if (entry.visible) {
          entry.state = newState
        }
      })
      this.entryStateChange(this.entry)
    },
    /**
     * Handle child entry's state change
     */
    handleChildSelect() {
      // when check state of a child component changed, check to see if
      // all/some/none of them are selected and change this own state
      // depending on them. The watcher tells it to parent.
      const states = new Set(this.entry.children.map(c => c.state))
      if (states.size > 1) {
        this.entry.state = 'intermediate'
      } else {
        this.entry.state = this.entry.children[0].state
      }
    },
    /**
     * Calculate and set child container's height
     *
     * When this entry is closed state, child height is just 0. When open,
     * there need a little calculation.
     */
    setChildHeight() {
      let childHeight = 0
      if (this.entry.open) {
        if (this.$refs.children) {
          for (const child of this.$refs.children) {
            if (!child.entry.visible) {
              continue
            }
            if (child.entry.children) {
              // folder has its children and its own height
              childHeight += child.childHeight + child.height
            } else {
              // leaf do not have children, so it takes only its own height.
              childHeight += child.height
            }
          }
        }
      }
      this.childHeight = childHeight
      this.$emit('open-change')
      this.$emit('visible-change')
    },
    /**
     * Handler for child open state change
     *
     * Child container height must be reevaluated when a child entry is
     * opened or closed. And parent should know it too (emitting event).
     */
    handleChildOpenChange() {
      this.setChildHeight()
      this.$emit('open-change')
    },
    handleChildVisibleChange() {
      this.setChildHeight()
      this.$emit('visible-change')
    },
    /**
     * Handler for child entries is rendered
     *
     * Adjusting child height should be triggered by open state change. But
     * $refs.children is empty when "entry.open" becomes true since they are not
     * mounted yet. Which means child height cannot be retrieved by watching
     * "entry.open".
     *
     * So prepare a transition for child entries and use the events for
     * trigering child height calculation. $refs.children exists when
     * "before-enter" fires.
     */
    beforeChildrenEnter() {
      this.setChildHeight()
    },
    /**
     * Handler for child entries is removed from DOM
     *
     * Same here as "before-enter"; watching "entry.open = false" cannot
     * have empty children state. "before-leave" might be the firstest
     * timing when $refs.children becomes empty.
     */
    beforeChildrenLeave() {
      this.setChildHeight()
    },
  },
  mounted() {
    if (this.entry.children) {
      this.setChildHeight()
    }
  },
}
</script>

<style lang="scss" scoped>
.entry {
  display: flex;
  align-items: center;
  position: relative;
  white-space: nowrap;

  .entry-element {
    cursor: pointer;

    padding-left: 10px;
    &:last-of-type {
      padding-right: 10px;
    }

    &.caret {
      flex: 0 0 20px;
      font-size: 16px;
      color: #546e7a;
    }
    &.check {
      color: #607d8b;
      font-size: 17px;
    }
    &.icon-folder {
      color: #fbc02d;
      font-size: 19px;
    }
  }

  &.selected {
    .entry-element {
      &.check {
        color: #26a69a;
      }
    }
  }
}

.children-container {
  overflow: hidden;
  transition: height 200ms ease;
}

// Since height transition of child entries container is done by simple
// CSS transition, Vue transition just plays a role not to disapper child
// entries immediately.
.v-leave-active {
  transition: opacity 0.5s;
}
.v-leave-to {
  opacity: 0;
}

.caret-icon {
  transition: transform 200ms ease;
}
.caret-open {
  transform: rotate(90deg);
}
</style>
